import * as React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
} from 'recharts';
import { CardHeader, MainBody } from '../fluxo/relatorio-fluxo-lista-styled';
import {
  BaSeBreakpointContext,
  BaSeHeading5,
  BaSeSmall1,
  BaSeTheme,
} from '@base/react';

const despesaColor = [
  '#F5AE22',
  '#f53952',
  '#6f0000',
  '#FF8D1A',
  '#FF4500',
  '#70670F',
  '#FF6F61',
  '#75373D',
  '#D2691E',
  '#583403',
  '#cf04aa',
  '#CD5C5C',
  '#FFA500',
  '#F08080',
  '#FFAE42',
  '#fa9704',
  '#FFCC33',
  '#FFA07A',
  '#E9967A',
];

const receitaColor = [
  '#0024A9',
  '#3CB371',
  '#9370DB',
  '#00BFFF',
  '#FF7F50',
  '#BA55D3',
  '#00FF7F',
  '#87CEEB',
  '#1E90FF',
  '#006400',
  '#800080',
  '#5F9EA0',
  '#6495ED',
  '#00CED1',
  '#008080',
  '#2E8B57',
  '#3CB371',
  '#4682b5',
  '#32CD32',
  '#8A2BE2',
  '#9400D3',
  '#4B0082',
];

const RADIAN = Math.PI / 180;

function shadeColor(color, percent) {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt((R * (100 + percent)) / 100);
  G = parseInt((G * (100 + percent)) / 100);
  B = parseInt((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = R > 0 ? R : 0;
  G = G > 0 ? G : 0;
  B = B > 0 ? B : 0;

  let RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16);
  let GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16);
  let BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
}

const CustomTooltip = (props) => {
  const { active, payload } = props;
  const { azulSebrae36 } = BaSeTheme.colors.institucionais;
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: BaSeTheme.colors.defaultColors.white,
          display: 'grid',
          gap: 10,
          width: 100,
          padding: 10,
          border: `1px solid ${azulSebrae36}`,
          borderRadius: 5,
        }}
      >
        <BaSeSmall1 isBold color={azulSebrae36}>
          {payload[0].payload?.name}
        </BaSeSmall1>
        <BaSeSmall1 isBold color={azulSebrae36}>
          {payload[0].payload?.valor?.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })}
        </BaSeSmall1>
      </div>
    );
  }

  return null;
};

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const Legend = ({ tipoLancamento, value, index, color }) => (
  <>
    <div
      id={`item-legenda-${tipoLancamento}-${index}`}
      style={{
        display: 'flex',
        gap: 10,
        margin: 5,
      }}
    >
      <div
        style={{
          background: color,
          width: '100%',
          height: 20,
          maxWidth: 20,
        }}
      />
      <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae60}>
        {value}
      </BaSeSmall1>
    </div>
  </>
);

export const GraficoRelatorioCategoria = ({
  dadosLancamento,
  isShowChartBar,
  setSvgReceita,
  setSvgDespesa,
  setIsDisabledExport,
  // eslint-disable-next-line
}) => {
  const { inTablet, inDesktop } = React.useContext(BaSeBreakpointContext);
  const [categorias, setCategorias] = React.useState([]);

  const isReceita = React.useMemo(
    () => dadosLancamento?.tipo === 'Receita',
    [dadosLancamento],
  );
  const chartBarRef = React.useRef(null);
  const chartPizzaRef = React.useRef(null);

  const handleUploadSVG = React.useCallback(
    (chartRef) => {
      const svgElement = chartRef?.current?.querySelector('div');

      if (svgElement) {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const blob = new Blob([svgData], {
          type: 'text/html',
        });
        dadosLancamento?.tipo === 'Receita' && setSvgReceita(blob);
        dadosLancamento?.tipo === 'Despesa' && setSvgDespesa(blob);
        setIsDisabledExport(false);
      }
    },
    [dadosLancamento, setIsDisabledExport, setSvgDespesa, setSvgReceita],
  );

  const listaGrafico = React.useCallback((lista) => {
    if (lista?.length < 11) {
      return lista;
    }

    lista.sort((a, b) => b.valor - a.valor);
    const maioresValores = lista.slice(0, 11);
    const menoresValores = lista.slice(11);
    const somaMenoresValores = menoresValores.reduce(
      (acc, obj) => acc + obj.valor,
      0,
    );

    return [
      ...maioresValores,
      {
        name: 'Outras categorias',
        valor: somaMenoresValores,
        fill: '#00008B',
      },
    ];
  }, []);

  const getParentWidth = React.useCallback((element) => {
    if (!element) return 600;
    return element - 16;
  }, []);

  React.useEffect(() => {
    if (!dadosLancamento) return;
    const listaCategorias = dadosLancamento?.listaCategorias?.map((item, i) => {
      const color = isReceita ? receitaColor[i] : despesaColor[i];
      return {
        name: item?.categoria,
        valor: Math.abs(item?.valorTotalCategoria),
        fill: color,
      };
    });

    setCategorias(listaGrafico(listaCategorias));
  }, [dadosLancamento, isReceita, listaGrafico]);

  React.useEffect(() => {
    if (!dadosLancamento) return;
    setIsDisabledExport(true);
    setTimeout(
      () => handleUploadSVG(!isShowChartBar ? chartPizzaRef : chartBarRef),
      1500,
      [],
    );
  }, [dadosLancamento, handleUploadSVG, isShowChartBar, setIsDisabledExport]);

  return (
    <>
      <div style={{ marginBottom: 36 }}>
        <MainBody>
          <CardHeader>
            <BaSeHeading5 isBold color=" #243342">
              {dadosLancamento?.tipo ?? ''}
            </BaSeHeading5>
          </CardHeader>
          {!isShowChartBar && (
            <div ref={chartPizzaRef}>
              <div
                style={{ display: inDesktop ? 'flex' : 'grid', padding: 20 }}
              >
                <PieChart
                  width={
                    inTablet
                      ? 550
                      : getParentWidth(chartPizzaRef?.current?.offsetWidth)
                  }
                  barSize={600}
                  height={400}
                >
                  <Pie
                    data={categorias}
                    cx={200}
                    cy={200}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={inTablet ? 70 : 60}
                    dataKey="valor"
                    isUpdateAnimationActive={false}
                    isAnimationActive={false}
                  >
                    {categorias?.map((item, index) => (
                      <Cell key={`cell-${index}`} fill={item?.fill} />
                    ))}
                  </Pie>
                  <Pie
                    isUpdateAnimationActive={false}
                    isAnimationActive={false}
                    data={categorias}
                    dataKey="valor"
                    cx={200}
                    cy={200}
                    innerRadius={inTablet ? 80 : 65}
                    outerRadius={inTablet ? 100 : 80}
                    label={({ value }) =>
                      `${value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}                        `
                    }
                  >
                    {categorias?.map((item, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={shadeColor(item?.fill, 30)}
                      />
                    ))}
                  </Pie>
                </PieChart>
                <div
                  id="legenda"
                  style={{
                    marginTop: 'auto',
                    marginBottom: 'auto',
                    padding: 10,
                    display: 'grid',
                    gridTemplateColumns: inTablet ? '1fr 1fr' : '1fr',
                  }}
                >
                  {categorias?.map((item, i) => (
                    <Legend
                      key={i}
                      index={i}
                      tipoLancamento={dadosLancamento?.tipo}
                      color={item?.fill}
                      value={`${
                        item?.name ?? ''
                      } (${item?.valor?.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })})`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          {isShowChartBar && (
            <div ref={chartBarRef}>
              <div
                style={{
                  display: inDesktop ? 'flex' : 'grid',
                  padding: inTablet ? 25 : 10,
                }}
              >
                <BarChart
                  width={
                    inDesktop
                      ? 550
                      : getParentWidth(chartBarRef?.current?.offsetWidth)
                  }
                  height={inTablet ? 400 : 300}
                  data={categorias}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="valor" minPointSize={5}></Bar>
                </BarChart>
                <div
                  id="legenda"
                  style={{
                    marginTop: 'auto',
                    marginBottom: 'auto',
                    padding: 10,
                    display: 'grid',
                    gridTemplateColumns: inTablet ? '1fr 1fr' : '1fr',
                  }}
                >
                  {categorias?.map((item, i) => (
                    <>
                      <Legend
                        key={i}
                        index={i}
                        tipoLancamento={dadosLancamento?.tipo}
                        color={item?.fill}
                        value={`${i} - ${
                          item?.name ?? ''
                        } (${item?.valor?.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })})`}
                      />
                    </>
                  ))}
                </div>
              </div>
            </div>
          )}
        </MainBody>
      </div>
    </>
  );
};
